import type React from "react";
import { type FormEvent, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
	type AuthenticatedUser,
	fetchDevLoginStatus,
	fetchPasswordResetStatus,
	getStoredAuthToken,
	loginAsDevelopmentUser,
	loginWithEmailPassword,
	registerWithEmailPassword,
} from "../../Auth/serverSessionApi.ts";
import { useUiI18n } from "../../I18n";
import { clientKernelContext } from "../../Kernel/ClientKernel.ts";
import { useClientDispatch, useClientSelector } from "../../Redux/clientHooks.ts";
import {
	setImpersonatedBy,
	setUserId,
	setUserLanguage,
	setUserProfile,
	setUserRole,
	userLanguageSelector,
} from "../../Redux/clientSlice.ts";
import { fetchFirebaseSocialConfig, type FirebaseSocialConfig } from "./FirebaseSocialConfig.ts";
import "./LoginScreen.css";

export const LoginScreen: React.FC<{ returnTo?: string }> = ({ returnTo }) => {
	const kernel = useContext(clientKernelContext);
	if (!kernel) throw new Error("Client kernel context is required");
	const navigate = useNavigate();
	const dispatch = useClientDispatch();
	const language = useClientSelector(userLanguageSelector);
	const { t } = useUiI18n();
	const [mode, setMode] = useState<"login" | "signup">("login");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [username, setUsername] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [devSubmitting, setDevSubmitting] = useState(false);
	const [socialSubmitting, setSocialSubmitting] = useState(false);
	const [devLoginEnabled, setDevLoginEnabled] = useState(false);
	const [passwordResetEnabled, setPasswordResetEnabled] = useState(false);
	const [socialConfig, setSocialConfig] = useState<FirebaseSocialConfig | null>(null);
	const busy = submitting || devSubmitting || socialSubmitting;
	const canSubmit = useMemo(() => {
		const minimum = mode === "signup" ? 12 : 6;
		return (
			email.trim().length > 0 &&
			password.length >= minimum &&
			password.length <= 128 &&
			(mode === "login" || username.trim().length > 0)
		);
	}, [email, mode, password.length, username]);

	useEffect(() => {
		let active = true;
		void fetchDevLoginStatus().then((enabled) => {
			if (active) setDevLoginEnabled(enabled);
		});
		void fetchPasswordResetStatus().then((enabled) => {
			if (active) setPasswordResetEnabled(enabled);
		});
		void fetchFirebaseSocialConfig().then((config) => {
			if (active) setSocialConfig(config);
		});
		return () => {
			active = false;
		};
	}, []);

	const completeAuth = useCallback(
		async (user: AuthenticatedUser) => {
			await kernel.client.clearStore();
			kernel.matchesClient.setAuthToken(getStoredAuthToken());
			dispatch(setUserId(user.id));
			dispatch(setUserProfile({ username: user.username, language: user.language }));
			dispatch(setUserRole(user.role));
			dispatch(setImpersonatedBy(user.impersonatedBy));
			void navigate(returnTo ?? "/dashboard");
		},
		[dispatch, kernel, navigate, returnTo],
	);

	const onSubmit = useCallback(
		async (event: FormEvent) => {
			event.preventDefault();
			if (!canSubmit || busy) return;
			try {
				setSubmitting(true);
				const user =
					mode === "login"
						? await loginWithEmailPassword(email, password)
						: await registerWithEmailPassword(email, password, username, language);
				await completeAuth(user);
			} catch (error) {
				console.error(error);
				toast.error(error instanceof Error ? error.message : t("auth.error"));
			} finally {
				setSubmitting(false);
			}
		},
		[busy, canSubmit, completeAuth, email, language, mode, password, t, username],
	);

	const onGoogleLogin = useCallback(async () => {
		if (!socialConfig || busy) return;
		try {
			setSocialSubmitting(true);
			const { loginWithGoogleFirebase } = await import("./FirebaseSocialLogin.ts");
			await completeAuth(await loginWithGoogleFirebase(socialConfig));
		} catch (error) {
			console.error(error);
			toast.error(t("auth.socialError"));
		} finally {
			setSocialSubmitting(false);
		}
	}, [busy, completeAuth, socialConfig, t]);

	const onDevLogin = useCallback(async () => {
		if (busy) return;
		try {
			setDevSubmitting(true);
			await completeAuth(await loginAsDevelopmentUser());
		} catch (error) {
			console.error(error);
			toast.error(error instanceof Error ? error.message : t("auth.error"));
		} finally {
			setDevSubmitting(false);
		}
	}, [busy, completeAuth, t]);

	return (
		<main
			className="login-world"
			style={{ backgroundImage: "url('/images/assets/login/citadel-card-login.webp')" }}
		>
			<div className="login-world__shade" />
			<div className="login-world__layout">
				<header className="login-world__brand">
					<div className="login-world__sigil" aria-hidden="true">
						✦
					</div>
					<p className="login-world__brand-name">{t("app.title")}</p>
				</header>
				<div className="login-world__content">
					<div className="login-world__introduction">
						<p className="login-world__eyebrow">{t("auth.heroEyebrow")}</p>
						<h1>{t("auth.heroTitle")}</h1>
						<p>{t("auth.heroDescription")}</p>
					</div>
					<form className="login-world__form" onSubmit={onSubmit}>
						<div className="login-world__form-top">
							<span>{t("auth.formEyebrow")}</span>
							<label className="login-world__language">
								<span>{t("common.language")}</span>
								<select
									aria-label={t("common.language")}
									value={language}
									onChange={(event) =>
										dispatch(setUserLanguage(event.target.value === "nl" ? "nl" : "en"))
									}
								>
									<option value="nl">{t("language.nl")}</option>
									<option value="en">{t("language.en")}</option>
								</select>
							</label>
						</div>
						<h2>{mode === "login" ? t("auth.signIn") : t("auth.signUp")}</h2>
						<p className="login-world__form-intro">
							{t(mode === "login" ? "auth.signInIntro" : "auth.signUpIntro")}
						</p>
						{socialConfig && (
							<>
								<button
									type="button"
									className="login-world__google"
									onClick={onGoogleLogin}
									disabled={busy}
								>
									<span className="login-world__google-icon" aria-hidden="true">
										G
									</span>
									{socialSubmitting ? t("auth.signingIn") : t("auth.google")}
								</button>
								<div className="login-world__divider">
									<span>{t("auth.orEmail")}</span>
								</div>
							</>
						)}
						<div className="login-world__fields">
							{mode === "signup" && (
								<label>
									<span>{t("auth.name")}</span>
									<input
										name="username"
										autoComplete="nickname"
										maxLength={40}
										value={username}
										onChange={(event) => setUsername(event.target.value)}
										placeholder={t("auth.name")}
									/>
								</label>
							)}
							<label>
								<span>{t("auth.email")}</span>
								<input
									name="email"
									autoComplete="email"
									type="email"
									value={email}
									onChange={(event) => setEmail(event.target.value)}
									placeholder={t("auth.email")}
								/>
							</label>
							<label>
								<span>{t("auth.password")}</span>
								<input
									name="password"
									autoComplete={mode === "login" ? "current-password" : "new-password"}
									type="password"
									minLength={mode === "signup" ? 12 : 6}
									maxLength={128}
									value={password}
									onChange={(event) => setPassword(event.target.value)}
									placeholder={t("auth.password")}
								/>
							</label>
						</div>
						{mode === "login" && passwordResetEnabled && (
							<Link className="login-world__forgot" to="/forgot-password">
								{t("auth.forgotPassword")}
							</Link>
						)}
						<button className="login-world__submit" type="submit" disabled={!canSubmit || busy}>
							{submitting
								? t(mode === "login" ? "auth.signingIn" : "auth.creatingAccount")
								: t(mode === "login" ? "auth.signIn" : "auth.signUp")}
							<span aria-hidden="true">→</span>
						</button>
						<button
							className="login-world__switch"
							type="button"
							onClick={() => setMode((previous) => (previous === "login" ? "signup" : "login"))}
							disabled={busy}
						>
							{t(mode === "login" ? "auth.needAccount" : "auth.haveAccount")}
						</button>
						{devLoginEnabled && (
							<button
								className="login-world__dev"
								type="button"
								onClick={onDevLogin}
								disabled={busy}
							>
								{devSubmitting ? t("auth.signingIn") : t("auth.developerLogin")}
							</button>
						)}
					</form>
				</div>
				<footer className="login-world__footer">
					<a href="/about.html">{t("auth.about")}</a>
					<a href="/privacy.html">{t("auth.privacy")}</a>
					<a href="/terms.html">{t("auth.terms")}</a>
				</footer>
			</div>
		</main>
	);
};
